import { createLogger } from '@/lib/logger';

const log = createLogger('EVENTS');

/**
 * Simple React Native-safe event emitter for app-internal updates.
 * No window or Node dependencies.
 */

type EventListener = (...args: any[]) => void;

class EventEmitter {
  private listeners: Map<string, Set<EventListener>> = new Map();

  on(event: string, listener: EventListener): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener);

    // Return unsubscribe function
    return () => this.off(event, listener);
  }

  off(event: string, listener: EventListener): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(listener);
      if (eventListeners.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  emit(event: string, ...args: any[]): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((listener) => {
        try {
          listener(...args);
        } catch (error) {
          log.error(`[EventEmitter] Error in listener for ${event}:`, error);
        }
      });
    }
  }

  removeAllListeners(event?: string): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

// Singleton instance for app-wide events
export const events = new EventEmitter();

// Event types for type safety
export const EventTypes = {
  LEADERBOARD_UPDATED: 'leaderboard-updated',
} as const;
